import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, FileText, CheckCircle2, XCircle, Loader2,
  Trash2, BookOpen, AlertTriangle, DownloadCloud,
} from "lucide-react";

const token = () => localStorage.getItem("token");

const CSATConceptNotesAdminPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [chapters, setChapters] = useState({ modules: [], total_chapters: 0 });
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const loadChapters = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/csat-concept-notes/chapters`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setChapters(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadChapters(); }, []);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.name.toLowerCase().endsWith(".docx"));
    setFiles(picked);
    setResult(null);
  };

  const upload = async () => {
    if (!files.length) { toast.error("Pick .docx files first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(
        `${API_BASE}/csat-concept-notes/admin/upload?replace=${replace}`,
        fd,
        { headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" } }
      );
      setResult(data);
      toast.success(`Imported ${data.imported} chapter(s)${data.failed ? `, ${data.failed} failed` : ""}`);
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      await loadChapters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const syncFromPreview = async () => {
    if (!window.confirm("Copy ALL concept notes + images from PREVIEW into this environment? This replaces what's here. Run this on PRODUCTION after a deploy.")) return;
    setSyncing(true);
    setSyncStatus(null);
    try {
      await axios.post(`${API_BASE}/csat-concept-notes/admin/sync-from-preview`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success("Sync started — pulling from preview…");
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API_BASE}/csat-concept-notes/admin/sync-status`, {
            headers: { Authorization: `Bearer ${token()}` },
          });
          setSyncStatus(data);
          if (data.state === "complete" || data.state === "error") {
            clearInterval(poll);
            setSyncing(false);
            if (data.state === "complete") { toast.success(`Sync complete · ${data.chapters} chapters, ${data.images_done} images`); await loadChapters(); }
            else toast.error(`Sync failed: ${data.error || "unknown"}`);
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setSyncing(false);
      toast.error(e?.response?.data?.detail || "Sync failed to start");
    }
  };


  const wipeAll = async () => {
    if (!window.confirm("Delete ALL uploaded CSAT concept notes? This cannot be undone.")) return;
    try {
      const { data } = await axios.delete(`${API_BASE}/csat-concept-notes/admin/all`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success(`Deleted ${data.deleted} chapter(s)`);
      await loadChapters();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" data-testid="ccn-admin-back">
          <ArrowLeft className="w-4 h-4" /> Admin Dashboard
        </button>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">CSAT Concept Notes</h1>
            <p className="text-slate-400 text-sm">Upload .docx chapter files · {chapters.total_chapters} chapters live</p>
          </div>
        </div>

        {/* Upload card */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader><CardTitle className="text-base text-white">Bulk upload chapters</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="cursor-pointer rounded-xl border-2 border-dashed border-slate-700 hover:border-indigo-500 p-8 text-center transition-colors"
              data-testid="ccn-dropzone"
            >
              <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-sm text-slate-300">Click to choose <b>.docx</b> concept-note files</p>
              <p className="text-xs text-slate-500 mt-1">You can select many at once</p>
              <input ref={fileRef} type="file" accept=".docx" multiple className="hidden" onChange={onPick} data-testid="ccn-file-input" />
            </div>

            {files.length > 0 && (
              <div className="rounded-lg bg-slate-800/60 p-3 max-h-40 overflow-y-auto space-y-1" data-testid="ccn-selected">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <FileText className="w-3.5 h-3.5 text-indigo-400" /> {f.name}
                  </div>
                ))}
                <p className="text-xs text-slate-500 pt-1">{files.length} file(s) selected</p>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="ccn-replace-toggle" />
              <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Replace ALL existing chapters before importing (wipes current, then loads these)</span>
            </label>

            <div className="flex items-center gap-3">
              <Button onClick={upload} disabled={uploading || !files.length} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ccn-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4 mr-2" /> Import {files.length || ""} chapter(s)</>}
              </Button>
              <Button onClick={wipeAll} variant="outline" className="border-rose-800 text-rose-300 hover:bg-rose-950" data-testid="ccn-wipe-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete all
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sync preview -> production */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader><CardTitle className="text-base text-white">Sync Preview → Production</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-400">Run this <b>on production after a deploy</b> to copy all chapters + images from preview — no re-uploading needed.</p>
            <div className="flex items-center gap-3">
              <Button onClick={syncFromPreview} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ccn-sync-btn">
                {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing…</> : <><DownloadCloud className="w-4 h-4 mr-2" /> Sync from Preview</>}
              </Button>
              {syncStatus && (
                <span className="text-sm text-slate-300" data-testid="ccn-sync-status">
                  {syncStatus.state} · {syncStatus.chapters || 0} chapters · {syncStatus.images_done || 0}/{syncStatus.images_total || 0} images
                  {syncStatus.error ? ` · ${syncStatus.error}` : ""}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Import result */}
        {result && (
          <Card className="bg-slate-900 border-slate-800" data-testid="ccn-result">
            <CardHeader><CardTitle className="text-base text-white">Import result · {result.imported} ok / {result.failed} failed</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {result.results.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {r.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />}
                  <span className="text-slate-300">{r.chapter || r.filename}</span>
                  {r.status === "ok" ? (
                    <span className="text-slate-500 text-xs">· {r.module} · {r.subsections} topics · {r.examples} examples</span>
                  ) : (
                    <span className="text-rose-400 text-xs">· {r.error}</span>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Live chapters */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader><CardTitle className="text-base text-white">Live chapters ({chapters.total_chapters})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {chapters.modules.length === 0 ? (
              <p className="text-sm text-slate-500">No chapters uploaded yet.</p>
            ) : chapters.modules.map((m) => (
              <div key={m.module}>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-indigo-300">{m.module}</p>
                  <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-700">{m.chapters.length}</Badge>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {m.chapters.map((c) => c.chapter).join(" · ")}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CSATConceptNotesAdminPage;
